import * as React from "react"
import {
  Box,
  IconButton,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  BoxProps
} from "@chakra-ui/react"
import { useEmojiPicker } from "../../../hooks/useEmojiPicker"
import { ExpandingInput } from "../../common"
import chroma from "chroma-js"
import { FiSend, FiUpload } from "react-icons/fi"
import { useRecoilState, useRecoilValue } from "recoil"
import { urlIfy } from "../../../util"
import layoutStates from "../../../recoil/atoms/layout"
import { resizeInput } from "../../../util/resizeInput"
import * as Yup from "yup"
import { sanitize } from "../../../util/sanitize"
import { getFocus } from "../../../util/getFocus"
import { brandingSelector, roomSelector } from "../../../recoil/atoms/room"
import { ConsoleLogger } from "../../../util/logger"
import useLocalUserHasPermissions from "../../../hooks/useLocalUserHasPermissions"
import { isRGBAcolor, replaceOpacity, getOpacity } from "../../../util"
import { useTranslation } from "react-i18next"
import { chatAttachmentDataAtom, chatAttachmentReplyDataAtom, localUserMessageResponseToken } from "src/recoil/atoms/chats"
import { convertToBase64 } from "../chat/utils/base64"
import { ToastConfigProps, useCustomToast } from "src/hooks/useToast"
import { useIsVideoMimeType } from "../chat/utils/useIsVideo"
import useConfig from "src/hooks/useConfig"
import usePrevious from "../chat/utils/usePrevious"

const logger = new ConsoleLogger()

interface MessageInputProps {
  inputId: string
  parentMessageId: string
  userId: any
  callbackEmoji: (e?: any) => void
  sendMessage: (message: string, parentMessageId: string, userId: any) => void
  inputStyle?: React.CSSProperties
  inputLeftElementStyle?: React.CSSProperties
  inputRightElementStyle?: React.CSSProperties
  positionModal: {
    x: string | number
    y: string | number
  }
  placeholder: string
  isMobile?: boolean
  isTransparent?: boolean
  topPositionError?: string
  stylesEmodjiContainer?: BoxProps
  offFocusFirstRender?: boolean
  hideEmodji?: boolean
  isReply?: boolean
  noAttachment?: boolean
}

export const MessageInput: React.FC<MessageInputProps> = ({
  inputId,
  parentMessageId,
  userId,
  callbackEmoji,
  sendMessage,
  positionModal,
  placeholder,
  inputStyle,
  inputLeftElementStyle,
  inputRightElementStyle,
  isMobile,
  isTransparent,
  topPositionError,
  stylesEmodjiContainer,
  offFocusFirstRender,
  hideEmodji,
  isReply,
  noAttachment
}) => {
  const { t } = useTranslation()
  const { chatAttachment, chatAttachmentVideo } = useConfig()
  const roomStyles = useRecoilValue(brandingSelector)
  const room = useRecoilValue(roomSelector)
  const grayBg = chroma("#0e233e").alpha(0.25).hex()
  const [text, setText] = React.useState<string>("")
  const [error, setError] = React.useState<string>("")
  const fileUploadRef = React.useRef<null | HTMLInputElement>(null)

  const [attachment, saveAttachment] = useRecoilState(chatAttachmentDataAtom)
  const [replyAttachment, setReplyAttachment] = useRecoilState(chatAttachmentReplyDataAtom)
  const [sendDisabled, setSendDisabled] = React.useState(false)
  const responseToken = useRecoilValue(localUserMessageResponseToken)
  const prevResponseToken = usePrevious(responseToken)
  const allowList = `image/png, image/jpeg, image/gif, ${chatAttachmentVideo ? ".mp4, .mov,": ""}`


  React.useEffect(() => {
    if (!fileUploadRef.current) return
    if (attachment.data === "" && fileUploadRef.current) {
      fileUploadRef.current.value = null
    }
  }, [attachment.data])

  React.useEffect(() => {
    if (!fileUploadRef.current) return
    if (replyAttachment.data === "" && fileUploadRef.current) {
      fileUploadRef.current.value = null
    }
  }, [replyAttachment.data])

  React.useEffect(() => {
    if (responseToken !== prevResponseToken) {
      setSendDisabled(false)
    }
  }, [responseToken])

  const [, setChatMessageHeight] = useRecoilState(
    layoutStates.chatMessageHeight
  )
  const { canSendChat: hasChatSendPermission } = useLocalUserHasPermissions()

  const focusInput = React.useCallback((): void => {
    getFocus(inputId)
  }, [inputId])

  const onEmojiClick = React.useCallback(
    (event, emojiObject) => {
      const space = text.length > 0 ? " " : ""
      setText(`${text}${space}${emojiObject.emoji}`)
      focusInput()
    },
    [focusInput, text]
  )

  const callbackMessageInput = React.useCallback(
    e => {
      callbackEmoji(e)
      focusInput()
    },
    [callbackEmoji, focusInput]
  )

  const { EmojiTrigger, EmojiModal, closeModal } = useEmojiPicker(
    onEmojiClick,
    callbackMessageInput,
    isMobile
  )

  const schema = Yup.string()
    .max(1024, "max. 1024 char")
    .test("nospace", " ", function (value) {
      const text = value.split("")
      const haveSpace = element => element !== " "
      return text.some(haveSpace)
    })
    .nullable()

  const handleChat = React.useCallback(async () => {
    if (sendDisabled || (!text.length && !attachment.data.length)) return
    setSendDisabled(true)

    try {
      const target = document.querySelector(".expanding-input")
      if (text && text.length > 0) {
        const messagrArr = text.split(" ")
        const alteredMessageArray = messagrArr.map(x => urlIfy(x))
        const processedText = alteredMessageArray.join(" ")
        const validProcessedText = await schema.validate(processedText)
        if (sanitize(validProcessedText) !== "") {
          sendMessage(validProcessedText, parentMessageId, userId)
          setText(() => "")
        } else {
          setText(() => "")
        }
        setError("")
        resizeInput(target, 38, true)
      } else {
        sendMessage("", parentMessageId, userId)
      }

      resizeInput(target, 38, true)
    } catch (e) {
      logger.error(e)
      setError(e.message)
    }
  }, [text, schema, sendMessage, parentMessageId, userId, sendDisabled])

  const handleFileUploadClick = () => {
    fileUploadRef.current.click()
  }

  const config: ToastConfigProps = {
    toastOptions: {
      containerStyle: {
        margin: "0.1rem"
      }
    },
    titleStyles: {
      fontWeight: 700
    },
    position: "bottom-right"
  }

  const showToastMessage = useCustomToast(config)

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.currentTarget.files[0]

    const MAX_SIZE_IMAGE = 10 * 1024 * 1024; // 10MB
    const MAX_SIZE_VIDEO = 50 * 1024 * 1024; // 50MB

    if (!file) return

    const fileIsVideo = useIsVideoMimeType(file.type)
    const maxSize = fileIsVideo ? MAX_SIZE_VIDEO : MAX_SIZE_IMAGE

    if (file.size > maxSize) {
      showToastMessage(
        `The ${fileIsVideo ? "video" : "image"} is too large. Maximum allowed size is ${
          maxSize / (1024 * 1024)
        }MB.`
      )
      return
    }

    if (isReply) {
      convertToBase64(e.currentTarget.files[0], setReplyAttachment)
    } else {
      convertToBase64(e.currentTarget.files[0], saveAttachment)
    }
  }

  const handleKeyPress = React.useCallback(
    (event: React.KeyboardEvent) => {
      const target = document.querySelector(
        ".expanding-input"
      ) as HTMLTextAreaElement
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault()
        handleChat()
        closeModal(false)
        resizeInput(target, 38, true)
      }
      if (event.key === "Enter" && event.shiftKey) {
        event.preventDefault()
        /*  handleChat() */
        target.value += "\r\n"
        closeModal(false)
        /* resizeInput(target, 38, true) */
      }
    },
    [closeModal, handleChat]
  )

  React.useEffect(() => {
    const target = document.querySelector(".expanding-input")
    const height = parseFloat(target?.["style"]?.height?.split("px")?.[0])

    if (!!!text) resizeInput(target, 38, true)

    setChatMessageHeight(height)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text])

  React.useEffect(() => {
    if (!offFocusFirstRender) {
      focusInput()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  React.useEffect(() => {
    if (!hasChatSendPermission) {
      closeModal(false)
      setText("")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasChatSendPermission])

  const borderColor = React.useMemo<string>(() => {
    if (error) {
      return roomStyles?.servicePanelElementWarningText ?? "red"
    }
    if (isRGBAcolor(roomStyles?.servicePanelElementBorder)) {
      return getOpacity(roomStyles?.servicePanelElementBorder) > 0.3
        ? replaceOpacity(
            roomStyles?.servicePanelElementBorder,
            getOpacity(roomStyles?.servicePanelElementBorder) - 0.3
          )
        : replaceOpacity(roomStyles?.servicePanelElementBorder, 0.1)
    }
    return `${roomStyles?.servicePanelElementBorder}b3` ?? "inherit"
  }, [
    error,
    roomStyles?.servicePanelElementBorder,
    roomStyles?.servicePanelElementWarningText
  ])

  return (
    <>
      <Box position="relative" zIndex={isMobile ? 10000001 : 1000000000}>
        <Box
          position="fixed"
          top={positionModal.y}
          width={isMobile ? "200px" : "450px"}
          height={isMobile && "100px"}
          right={!isMobile && positionModal.x}
          left={isMobile && `${positionModal.x}px`}
          zIndex={10000002}
          marginLeft={isMobile && "30px"}
          {...stylesEmodjiContainer}
        >
          <EmojiModal />
        </Box>
      </Box>

      <InputGroup
        width="100%"
        height="100%"
        d="flex"
        justifyContent="space-between"
        alignItems="flex-end"
        pos="relative"
        /* marginTop={error && "0.5rem"} */
      >
        {error && (
          <Box
            right="0px"
            top={topPositionError ?? "-17px"}
            pos="absolute"
            fontSize="0.7rem"
            color={roomStyles?.servicePanelElementWarningText ?? "red"}
            marginRight="1rem"
          >
            {error}
          </Box>
        )}
        {hasChatSendPermission && !hideEmodji && (
          <InputLeftElement
            color={roomStyles?.servicePanelElementPrimaryText ?? "#718096"}
            top="-1.5px"
            children={
              <EmojiTrigger
                styleIcon={{ opacity: text.length > 0 ? 1 : 0.75 }}
              />
            }
            style={{ ...inputLeftElementStyle }}
          />
        )}

        <ExpandingInput
          id={inputId}
          paddingTop="8px"
          height="38px"
          fontSize="sm"
          backgroundColor={
            isTransparent
              ? "none"
              : roomStyles?.servicePanelElementBackground ?? "white"
          }
          isDisabled={hasChatSendPermission ? false : true}
          className="expanding-input"
          _hover={{
            borderColor: error
              ? roomStyles?.servicePanelElementWarningText ?? "red"
              : roomStyles?.servicePanelElementBorder ?? grayBg
          }}
          alignItems="center"
          border="2px solid"
          borderColor={borderColor}
          style={{ ...inputStyle }}
          _focus={{
            borderColor: error
              ? roomStyles?.servicePanelElementWarningText ?? "red"
              : roomStyles?.servicePanelElementBorder ?? "gray.500"
          }}
          color={roomStyles?.servicePanelElementPrimaryText ?? "gray.500"}
          _placeholder={{
            color:
              room?.branding?.servicePanelElementPrimaryText &&
              room?.branding?.servicePanelElementPrimaryText,
            opacity: 0.5
          }}
          _disabled={{ opacity: 1 }}
          d="flex"
          margin="0"
          textAlign={hasChatSendPermission ? "unset" : "left"}
          onChange={event => {
            const target = event.target
            setText(target.value)
            resizeInput(event.target)
          }}
          rows={1}
          onKeyPress={event => {
            handleKeyPress(event)
            resizeInput(event.target)
          }}
          paddingLeft={
            hasChatSendPermission
              ? hideEmodji
                ? "0.5rem !important"
                : "2rem !important"
              : "0.5rem !important"
          }
          paddingRight={
            hasChatSendPermission
              ? hideEmodji
                ? "0.5rem !important"
                : "3.1rem !important"
              : "0.5rem !important"
          }
          placeholder={
            hasChatSendPermission
              ? t(placeholder)
              : t("Live chat currently disabled")
          }
          px="4"
          value={text}
        />
        {hasChatSendPermission && (
          <InputRightElement
            d="flex"
            paddingTop="0.2rem"
            style={{
              ...inputRightElementStyle,
              ...(!chatAttachment ? { justifyContent: "center" } : {}),
              ...(!chatAttachment ? { marginRight: "0" } : {})
            }}
            children={
              <>
                {!noAttachment && chatAttachment && (
                  <IconButton
                    aria-label="Upload an attachment"
                    icon={
                      <FiUpload
                        color="gray.500"
                        size="1.2em"
                        style={{ margin: 0 }}
                      />
                    }
                    variant="link"
                    opacity={text.length > 0 ? 1 : 0.75}
                    color={
                      roomStyles?.servicePanelElementPrimaryText ?? "gray.500"
                    }
                    _focus={{}}
                    onClick={handleFileUploadClick}
                    minW="unset"
                  />
                )}
                <IconButton
                  aria-label="Send message"
                  icon={
                    <FiSend
                      color="gray.500"
                      size="1.2em"
                      style={{ margin: 0 }}
                    />
                  }
                  variant="link"
                  opacity={text.length > 0 ? 1 : 0.75}
                  color={
                    roomStyles?.servicePanelElementPrimaryText ?? "gray.500"
                  }
                  _focus={{}}
                  onClick={!sendDisabled ? handleChat : null}
                  minW="unset"
                  disabled={sendDisabled}
                />
              </>
            }
          />
        )}
        {hasChatSendPermission && (
          <input
            ref={fileUploadRef}
            type="file"
            accept={allowList}
            onChange={handleFileUpload}
            style={{
              width: 0,
              height: 0,
              overflow: "hidden",
              position: "absolute"
            }}
          />
        )}
      </InputGroup>
    </>
  )
}
